import { useEffect, useMemo, useState } from 'react';
import DismissalStateControl from '../components/DismissalStateControl';
import ParentProfileCard from '../components/ParentProfileCard';
import PickupStudentsCard from '../components/PickupStudentsCard';
import SchoolAvatar from '../components/SchoolAvatar';
import SchoolLayout from '../components/SchoolLayout';
import { getGradeLabel, getStudentName } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';
import useSchoolDismissalLiveData from '../hooks/useSchoolDismissalLiveData';
import { isDismissalSimulationRunning } from '../lib/dismissalSimulation';

const PICKUP_CONTACT_TYPES = new Set(['parent', 'guardian', 'driver']);

function formatPickupContactName(contact) {
  return [contact?.f_name, contact?.l_name].filter(Boolean).join(' ').trim() || 'Unknown Contact';
}

function getPickupContactLabel(contact) {
  const type = String(contact?.type || '').toLowerCase();
  if (type === 'guardian') return 'Guardian';
  if (type === 'driver') return 'Driver';
  return 'Parent';
}

function isPickedUpStatus(status) {
  return ['picked_up', 'completed'].includes(String(status || '').toLowerCase());
}

function confirmRemoveFromZone(message) {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return true;
  return window.confirm(message);
}

function EmptyDismissalSection({ className = '', title, subtitle, message }) {
  return (
    <div className={`dc-section dc-empty-card ${className}`.trim()}>
      <h3 className="dc-section-title">{title}</h3>
      <p className="dc-section-subtitle">{subtitle}</p>
      <div className="dc-empty-copy">{message}</div>
    </div>
  );
}

export default function DismissalConsole({
  token,
  navigate,
  schoolId,
  user,
  dismissalActive = false,
  onDismissalActiveChange,
  bare = false,
}) {
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedLaneId, setSelectedLaneId] = useState(null);
  const [manualSearch, setManualSearch] = useState('');
  const [manualStudentSearch, setManualStudentSearch] = useState('');
  const [manualMarkingId, setManualMarkingId] = useState(null);
  const [manualStudentMarkingId, setManualStudentMarkingId] = useState(null);
  const [walkInMarkingId, setWalkInMarkingId] = useState(null);
  const [manualNotice, setManualNotice] = useState('');
  const {
    hasActivity,
    students,
    contacts,
    gradeLabelMap,
    consoleLanes,
    consoleNowCallingStudents,
    consoleStudentsHeadingToPickup,
    consoleDriversInLoadingZone,
    consoleRecentPickupContacts,
    consoleContactsById,
    consoleLinkedStudentsByContactId,
    consoleDismissalByStudentId,
    refreshLiveData,
  } = useSchoolDismissalLiveData({ schoolId, token });
  const simulatedDismissalRunning = isDismissalSimulationRunning(schoolId);
  const liveDismissalActive = dismissalActive || hasActivity || simulatedDismissalRunning;

  const selectedProfile = useMemo(
    () => {
      const contact = consoleContactsById[Number(selectedContactId)];
      if (!contact) return null;

      return {
        parent: contact,
        linkedStudents: consoleLinkedStudentsByContactId[Number(contact.id)] || [],
        dismissalByStudentId: consoleDismissalByStudentId,
      };
    },
    [consoleContactsById, consoleDismissalByStudentId, consoleLinkedStudentsByContactId, selectedContactId]
  );

  const {
    nowCallingStudents,
    studentsHeadingToPickup,
    driversInLoadingZone,
    recentPickupContacts,
  } = useMemo(() => {
    if (selectedLaneId === null) {
      return {
        nowCallingStudents: consoleNowCallingStudents,
        studentsHeadingToPickup: consoleStudentsHeadingToPickup,
        driversInLoadingZone: consoleDriversInLoadingZone,
        recentPickupContacts: consoleRecentPickupContacts,
      };
    }

    const target = Number(selectedLaneId);
    const matchesAny = (laneIds) => (laneIds || []).some((id) => Number(id) === target);

    return {
      nowCallingStudents: consoleNowCallingStudents.filter((student) => Number(student.laneId) === target),
      studentsHeadingToPickup: consoleStudentsHeadingToPickup.filter((student) => Number(student.laneId) === target),
      driversInLoadingZone: consoleDriversInLoadingZone.filter((driver) => matchesAny(driver.laneIds)),
      recentPickupContacts: consoleRecentPickupContacts.filter((pickup) => matchesAny(pickup.laneIds)),
    };
  }, [
    selectedLaneId,
    consoleNowCallingStudents,
    consoleStudentsHeadingToPickup,
    consoleDriversInLoadingZone,
    consoleRecentPickupContacts,
  ]);

  const laneActivityCounts = useMemo(() => {
    const counts = {};
    const bump = (laneId) => {
      if (laneId === null || laneId === undefined) return;
      const key = Number(laneId);
      counts[key] = (counts[key] || 0) + 1;
    };
    consoleNowCallingStudents.forEach((student) => bump(student.laneId));
    consoleStudentsHeadingToPickup.forEach((student) => bump(student.laneId));
    return counts;
  }, [consoleNowCallingStudents, consoleStudentsHeadingToPickup]);

  const totalLaneActivity = consoleNowCallingStudents.length + consoleStudentsHeadingToPickup.length;

  const zoneState = useMemo(() => {
    const contactById = new Map();
    const studentById = new Map();

    consoleDriversInLoadingZone.forEach((driver) => {
      if (driver.contactId) {
        contactById.set(Number(driver.contactId), driver);
      }

      (driver.linkedStudents || []).forEach((student) => {
        if (student?.id) studentById.set(Number(student.id), driver);
      });

      if (driver.studentId) {
        studentById.set(Number(driver.studentId), driver);
      }
    });

    return { contactById, studentById };
  }, [consoleDriversInLoadingZone]);

  useEffect(() => {
    if (selectedLaneId !== null && !consoleLanes.some((lane) => lane.id === Number(selectedLaneId))) {
      setSelectedLaneId(null);
    }
  }, [consoleLanes, selectedLaneId]);

  useEffect(() => {
    if (onDismissalActiveChange) {
      const nextActive = hasActivity || simulatedDismissalRunning;
      if (nextActive !== dismissalActive) {
        onDismissalActiveChange(nextActive);
      }
    }
  }, [dismissalActive, hasActivity, onDismissalActiveChange, simulatedDismissalRunning]);

  useEffect(() => {
    if (!liveDismissalActive) {
      setSelectedContactId(null);
    }
  }, [liveDismissalActive]);

  useEffect(() => {
    if (selectedContactId && !consoleContactsById[Number(selectedContactId)]) {
      setSelectedContactId(null);
    }
  }, [consoleContactsById, selectedContactId]);

  const manualCheckInResults = useMemo(() => {
    const query = manualSearch.trim().toLowerCase();
    if (!query) return [];

    return contacts
      .filter((contact) => PICKUP_CONTACT_TYPES.has(String(contact?.type || '').toLowerCase()))
      .filter((contact) => {
        const fields = [
          formatPickupContactName(contact),
          contact.email,
          contact.phone,
          contact.license_plate,
        ];

        return fields.some((field) => String(field || '').toLowerCase().includes(query));
      })
      .sort((left, right) => formatPickupContactName(left).localeCompare(formatPickupContactName(right)))
      .slice(0, 6);
  }, [contacts, manualSearch]);

  const manualStudentCheckInResults = useMemo(() => {
    const query = manualStudentSearch.trim().toLowerCase();
    if (!query) return [];

    return students
      .filter((student) => String(student?.status || '').toLowerCase() !== 'inactive')
      .filter((student) => {
        const fields = [
          getStudentName(student),
          getGradeLabel(student.grade, gradeLabelMap),
          student.student_email,
          student.student_phone,
        ];

        return fields.some((field) => String(field || '').toLowerCase().includes(query));
      })
      .sort((left, right) => getStudentName(left).localeCompare(getStudentName(right)))
      .slice(0, 6);
  }, [gradeLabelMap, manualStudentSearch, students]);

  const handleManualZoneCheckIn = async (contact) => {
    if (!contact?.id || manualMarkingId) return;

    setManualMarkingId(contact.id);
    setManualNotice('');

    try {
      const res = await apiFetch('/geofenced-data/manual-entry', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          parent_driver_id: contact.id,
        }),
      }, token);
      const calledCount = res.data?.called_student_ids?.length || 0;
      setManualNotice(`${formatPickupContactName(contact)} marked in zone${calledCount ? `; ${calledCount} students called` : ''}.`);
      setManualSearch('');
      await refreshLiveData();
    } catch (err) {
      setManualNotice(err.message || 'Unable to mark contact in zone.');
    } finally {
      setManualMarkingId(null);
    }
  };

  const handleManualZoneRemove = async (contact) => {
    if (!contact?.id || manualMarkingId) return;
    const name = formatPickupContactName(contact);
    if (!confirmRemoveFromZone(`Remove ${name} from the loading zone?`)) return;

    setManualMarkingId(contact.id);
    setManualNotice('');

    try {
      await apiFetch('/geofenced-data/manual-exit', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          parent_driver_id: contact.id,
        }),
      }, token);
      setManualNotice(`${name} removed from the loading zone.`);
      await refreshLiveData();
    } catch (err) {
      setManualNotice(err.message || 'Unable to remove contact from zone.');
    } finally {
      setManualMarkingId(null);
    }
  };

  const handleManualStudentZoneCheckIn = async (student) => {
    if (!student?.id || manualStudentMarkingId) return;

    setManualStudentMarkingId(student.id);
    setManualNotice('');

    try {
      const res = await apiFetch('/geofenced-data/manual-student-entry', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          student_id: student.id,
        }),
      }, token);
      const called = res.data?.called_student_ids?.includes(Number(student.id));
      setManualNotice(`${getStudentName(student)} marked with driver in pickup area${called ? '; student called' : ''}.`);
      setManualStudentSearch('');
      await refreshLiveData();
    } catch (err) {
      setManualNotice(err.message || 'Unable to mark student driver in pickup area.');
    } finally {
      setManualStudentMarkingId(null);
    }
  };

  const handleManualStudentZoneRemove = async (student, zoneDriver) => {
    if (!student?.id || manualStudentMarkingId || !zoneDriver) return;
    const studentName = getStudentName(student);
    const driverName = zoneDriver.contactId ? zoneDriver.name : 'untracked pickup';
    const message = zoneDriver.contactId
      ? `Remove ${driverName} from the loading zone for ${studentName}? This affects all linked students for this driver.`
      : `Remove ${studentName}'s untracked pickup from the loading zone?`;
    if (!confirmRemoveFromZone(message)) return;

    setManualStudentMarkingId(student.id);
    setManualNotice('');

    try {
      if (zoneDriver.contactId) {
        await apiFetch('/geofenced-data/manual-exit', {
          method: 'POST',
          body: JSON.stringify({
            school_id: schoolId,
            parent_driver_id: zoneDriver.contactId,
          }),
        }, token);
        setManualNotice(`${driverName} removed from the loading zone.`);
      } else {
        await apiFetch('/geofenced-data/manual-student-exit', {
          method: 'POST',
          body: JSON.stringify({
            school_id: schoolId,
            student_id: student.id,
          }),
        }, token);
        setManualNotice(`${studentName}'s untracked pickup removed from the loading zone.`);
      }
      await refreshLiveData();
    } catch (err) {
      setManualNotice(err.message || 'Unable to remove student driver from zone.');
    } finally {
      setManualStudentMarkingId(null);
    }
  };

  const handleWalkInPickup = async (student) => {
    if (!student?.id || walkInMarkingId || manualStudentMarkingId) return;

    const studentName = getStudentName(student);
    const message = `Mark ${studentName} as picked up by walk-in? Use this only when the pickup contact is physically with the student.`;
    if (!confirmRemoveFromZone(message)) return;

    setWalkInMarkingId(student.id);
    setManualNotice('');

    try {
      await apiFetch('/dismissal-status', {
        method: 'PUT',
        body: JSON.stringify({
          school_id: schoolId,
          student_id: student.id,
          status: 'picked_up',
        }),
      }, token);
      setManualNotice(`${studentName} marked picked up by walk-in.`);
      setManualStudentSearch('');
      await refreshLiveData();
    } catch (err) {
      setManualNotice(err.message || 'Unable to mark student picked up by walk-in.');
    } finally {
      setWalkInMarkingId(null);
    }
  };

  const content = (
    <div className="dc-board-shell">
      <div className={`dc-board ${selectedProfile ? 'is-dimmed' : ''}`}>
        <div className="dc-col dc-col-left">
          <div className="dc-section dc-lane-picker">
            <h3 className="dc-section-title">Lane Picker</h3>
            <p className="dc-section-hint">Choose a pickup lane to monitor, or view all</p>
            <div className="dc-lane-options">
              <button
                type="button"
                className={`dc-lane-chip ${selectedLaneId === null ? 'is-active' : ''}`}
                onClick={() => setSelectedLaneId(null)}
              >
                <span className="dc-lane-chip-name">All Lanes</span>
                <span className="dc-lane-chip-count">{totalLaneActivity}</span>
              </button>
              {consoleLanes.map((lane) => (
                <button
                  key={lane.id}
                  type="button"
                  className={`dc-lane-chip ${Number(selectedLaneId) === lane.id ? 'is-active' : ''}`}
                  onClick={() => setSelectedLaneId(lane.id)}
                >
                  <span className="dc-lane-chip-name">{lane.name}</span>
                  <span className="dc-lane-chip-count">{laneActivityCounts[lane.id] || 0}</span>
                </button>
              ))}
            </div>
            {consoleLanes.length === 0 ? (
              <div className="dc-empty-copy">No pickup lanes are configured for this school yet.</div>
            ) : null}
          </div>

          {liveDismissalActive ? (
            <div className="dc-section dc-now-calling">
              <h3 className="dc-section-title">Now Calling</h3>
              <span className="dc-badge dc-badge-orange">{nowCallingStudents.length} students ready for pickup</span>
              <p className="dc-section-hint dc-now-calling-hint">Student has been called and is ready for pickup</p>
              {nowCallingStudents.length ? (
                <div className="dc-card-list">
                  {nowCallingStudents.map((student) => (
                    <div key={student.id} className="dc-card dc-card-orange">
                      <SchoolAvatar name={student.name} seed={student.id} className="dc-avatar dc-avatar-ring" />
                      <div className="dc-card-info">
                        <div className="dc-card-name">{student.name}</div>
                        <div className="dc-card-sub">{student.grade}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="dc-empty-copy">No students have been called yet.</div>
              )}
            </div>
          ) : (
            <EmptyDismissalSection
              className="dc-now-calling dc-empty-card-left"
              title="Now Calling"
              subtitle="No students currently being called"
              message={'Students will appear here once parents\narrive and dismissal begins'}
            />
          )}

          {liveDismissalActive ? (
            <div className="dc-section">
              <PickupStudentsCard
                title="Sent to Carpool"
                students={studentsHeadingToPickup}
                subtitle={studentsHeadingToPickup.length ? undefined : 'No students currently sent to carpool'}
                hint=""
                emptyMessage={'Students will appear here after teachers\nsend them to carpool'}
              />
            </div>
          ) : (
            <EmptyDismissalSection
              className="dc-empty-students dc-empty-card-left"
              title="Sent to Carpool"
              subtitle="No students currently sent to carpool"
              message={'Students will appear here after teachers\nsend them to carpool'}
            />
          )}
        </div>

        <div className="dc-col dc-col-center">
          {liveDismissalActive ? (
            <div className="dc-section dc-manual-checkin">
              <div className="dc-manual-heading">
                <div>
                  <h3 className="dc-section-title">Manual Check-in</h3>
                  <p className="dc-section-hint">Search a pickup contact or student when GPS is unavailable.</p>
                </div>
              </div>
              <div className="dc-manual-group">
                <label className="dc-manual-search">
                  <span>⌕</span>
                  <input
                    type="text"
                    value={manualSearch}
                    onChange={(event) => setManualSearch(event.target.value)}
                    placeholder="Search parent, guardian, driver, phone, or plate"
                  />
                </label>
                {manualSearch.trim() ? (
                  <div className="dc-manual-results">
                    {manualCheckInResults.length ? manualCheckInResults.map((contact) => {
                      const contactInZone = zoneState.contactById.has(Number(contact.id));
                      const busy = Number(manualMarkingId) === Number(contact.id);

                      return (
                        <div key={contact.id} className="dc-manual-result-row">
                          <SchoolAvatar name={formatPickupContactName(contact)} seed={contact.id} className="dc-avatar" />
                          <div className="dc-card-info">
                            <div className="dc-card-name-sm">{formatPickupContactName(contact)}</div>
                            <div className="dc-card-sub">
                              {[getPickupContactLabel(contact), contact.phone || contact.email || contact.license_plate].filter(Boolean).join(' - ')}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`dc-manual-mark-btn ${contactInZone ? 'dc-manual-mark-btn-active' : ''}`}
                            disabled={busy}
                            onClick={() => {
                              if (contactInZone) handleManualZoneRemove(contact);
                              else handleManualZoneCheckIn(contact);
                            }}
                          >
                            {busy ? (contactInZone ? 'Removing...' : 'Marking...') : 'In zone'}
                          </button>
                        </div>
                      );
                    }) : (
                      <div className="dc-manual-empty">No pickup contacts match that search.</div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="dc-manual-group">
                <label className="dc-manual-search">
                  <span>⌕</span>
                  <input
                    type="text"
                    value={manualStudentSearch}
                    onChange={(event) => setManualStudentSearch(event.target.value)}
                    placeholder="Search student for driver override"
                  />
                </label>
                {manualStudentSearch.trim() ? (
                  <div className="dc-manual-results">
                    {manualStudentCheckInResults.length ? manualStudentCheckInResults.map((student) => {
                      const zoneDriver = zoneState.studentById.get(Number(student.id)) || null;
                      const dismissalStatus = consoleDismissalByStudentId[Number(student.id)] || null;
                      const status = String(dismissalStatus?.status || '').toLowerCase();
                      const onWay = status === 'sent';
                      const pickedUp = isPickedUpStatus(status);
                      const zoneBusy = Number(manualStudentMarkingId) === Number(student.id);
                      const walkInBusy = Number(walkInMarkingId) === Number(student.id);
                      const busy = zoneBusy || walkInBusy;
                      const disabled = busy || (!zoneDriver && (onWay || pickedUp));
                      const buttonLabel = zoneDriver
                        ? 'In zone'
                        : (pickedUp ? 'Picked up' : (onWay ? 'On way' : 'Driver here'));
                      const buttonClass = [
                        'dc-manual-mark-btn',
                        zoneDriver ? 'dc-manual-mark-btn-active' : '',
                        !zoneDriver && onWay ? 'dc-manual-mark-btn-status' : '',
                        !zoneDriver && pickedUp ? 'dc-manual-mark-btn-complete' : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <div key={student.id} className="dc-manual-result-row">
                          <SchoolAvatar name={getStudentName(student)} seed={student.id} className="dc-avatar" />
                          <div className="dc-card-info">
                            <div className="dc-card-name-sm">{getStudentName(student)}</div>
                            <div className="dc-card-sub">{getGradeLabel(student.grade, gradeLabelMap)}</div>
                          </div>
                          <div className="dc-manual-result-actions">
                            <button
                              type="button"
                              className={buttonClass}
                              disabled={disabled}
                              onClick={() => {
                                if (zoneDriver) handleManualStudentZoneRemove(student, zoneDriver);
                                else if (!onWay && !pickedUp) handleManualStudentZoneCheckIn(student);
                              }}
                            >
                              {zoneBusy ? (zoneDriver ? 'Removing...' : 'Marking...') : buttonLabel}
                            </button>
                            <button
                              type="button"
                              className="dc-manual-mark-btn dc-manual-walkin-btn"
                              disabled={busy || pickedUp}
                              onClick={() => handleWalkInPickup(student)}
                            >
                              {walkInBusy ? 'Marking...' : (pickedUp ? 'Picked up' : 'Walk-in pickup')}
                            </button>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="dc-manual-empty">No students match that search.</div>
                    )}
                  </div>
                ) : null}
              </div>
              {manualNotice ? <div className="dc-manual-notice">{manualNotice}</div> : null}
            </div>
          ) : null}

          {liveDismissalActive ? (
            <div className="dc-section dc-loading-zone">
              <h3 className="dc-section-title">Drivers in Loading Zone</h3>
              <span className="dc-badge dc-badge-grey">{driversInLoadingZone.length} drivers waiting</span>
              <p className="dc-section-hint dc-loading-zone-hint">Tap driver to view students</p>
              {driversInLoadingZone.length ? (
                <div className="dc-driver-grid">
                  {driversInLoadingZone.map((driver) => (
                    <button
                      key={driver.id || driver.contactId}
                      type="button"
                      className={`dc-card dc-card-driver ${driver.isStudentOverride ? 'dc-card-driver-override' : ''}`}
                      disabled={!driver.contactId}
                      onClick={() => {
                        if (driver.contactId) setSelectedContactId(driver.contactId);
                      }}
                      style={{
                        '--dc-driver-progress': `${driver.childrenCount ? Math.round((driver.loadedCount / driver.childrenCount) * 100) : 0}%`,
                      }}
                    >
                      <SchoolAvatar name={driver.name} seed={driver.contactId || driver.studentId} className="dc-avatar dc-avatar-ring" />
                      <div className="dc-card-info">
                        <div className="dc-card-name-sm">{driver.name}</div>
                        <div className="dc-card-sub">
                          {driver.isStudentOverride
                            ? getStudentName(driver.linkedStudents?.[0])
                            : `(${driver.loaded})`}
                        </div>
                      </div>
                      <span className="dc-zone-time-badge">{driver.inZoneLabel}</span>
                      {driver.contactId ? <span className="dc-arrow">→</span> : null}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="dc-empty-copy">Drivers will appear here once they enter the pickup area.</div>
              )}
            </div>
          ) : (
            <EmptyDismissalSection
              className="dc-loading-zone"
              title="Drivers in Loading Zone"
              subtitle="No drivers in the loading zone"
              message={'Drivers will appear here once\nthey enter the pickup area'}
            />
          )}
        </div>

        <div className="dc-col dc-col-right">
          {liveDismissalActive ? (
            <div className="dc-section dc-recent-pickups">
              <h3 className="dc-section-title">Recent Pickups</h3>
              <span className="dc-badge dc-badge-green">{recentPickupContacts.length} completed</span>
              <p className="dc-section-hint dc-recent-pickups-hint">Latest completed pickups</p>
              {recentPickupContacts.length ? (
                <div className="dc-card-list dc-pickup-scroll">
                  {recentPickupContacts.map((pickup) => (
                    <button
                      key={pickup.id}
                      type="button"
                      className="dc-card dc-card-green"
                      onClick={() => setSelectedContactId(pickup.contactId)}
                    >
                      <SchoolAvatar name={pickup.name} seed={pickup.contactId} className="dc-avatar dc-avatar-ring" />
                      <div className="dc-card-info">
                        <div className="dc-card-name-light">{pickup.name}</div>
                        <div className="dc-card-sub-light">{pickup.time}</div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="dc-empty-copy">Completed pickups will appear here once staff mark students picked up.</div>
              )}
            </div>
          ) : (
            <EmptyDismissalSection
              className="dc-recent-pickups"
              title="Recent Pickups"
              subtitle="No completed pickups yet"
              message={'Completed students will\nappear here once they are\npicked up'}
            />
          )}
        </div>
      </div>

      {liveDismissalActive && selectedProfile ? (
        <div className="dc-profile-overlay" onClick={() => setSelectedContactId(null)}>
          <div className="dc-profile-popout" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="dc-profile-back"
              onClick={() => setSelectedContactId(null)}
              aria-label="Back"
            >
              ←
            </button>

            <ParentProfileCard
              parent={selectedProfile.parent}
              linkedStudents={selectedProfile.linkedStudents}
              dismissalByStudentId={selectedProfile.dismissalByStudentId}
              className="sap-parent-profile-card-compact"
            />
          </div>
        </div>
      ) : null}
    </div>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="dismissal"
      breadcrumb="Dismissal Console"
      headerRight={(
        <DismissalStateControl
          active={dismissalActive}
          onToggle={() => onDismissalActiveChange?.(!dismissalActive)}
        />
      )}
    >
      {content}
    </SchoolLayout>
  );
}
